import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Home, Users } from "lucide-react";
import { InitialProfilePicture } from "./InitialProfilePicture";
import { useEffects } from "@/state/legacy_module_api";
import { useStore } from "@/state/legacy_module_api";
import { useDisplayPhotoKey } from "@/state/legacy_module_api";
import { parseJwt } from "@/state/legacy_module_api";
import { Contact } from "@/data/schemas";
import { ensureProfilePhotoCached } from "@/services/profilePhotoManager";

interface UserProfilePictureProps {
  user?: Contact;
  slot?: string;
  width?: number;
  src?: string;
  isGroup?: boolean;
  isCommunity?: boolean;
  style?: CSSProperties;
}

export function UserProfilePicture({
  user,
  slot,
  width,
  src,
  isGroup,
  isCommunity,
  style,
}: UserProfilePictureProps) {
  const { filesystem } = useEffects();
  const displayPhotoKey = useDisplayPhotoKey();
  const profilePhotoEntry = useStore((state) =>
    user?.userId ? (state as any).profilePhotos?.[user.userId] : undefined
  );
  const apiToken = useStore((state) => state.apiToken);
  const localDisplayPhotoUrl = useMemo(() => {
    try {
      return localStorage.getItem("displayPhotoUrl") || undefined;
    } catch {
      return undefined;
    }
  }, []);
  const fallbackUserId = useMemo(() => {
    if (!apiToken) return undefined;
    try {
      return parseJwt(apiToken as string)?.user_id;
    } catch {
      return undefined;
    }
  }, [apiToken]);
  const effectivePhoto = useMemo(() => {
    if (src) return { cachePath: undefined, url: src, key: undefined as string | undefined };
    // Always prefer local cache path
    const cachePath = profilePhotoEntry?.cachePath;
    const urlFallback =
      user?.displayPhoto ||
      profilePhotoEntry?.url ||
      (user?.userId === fallbackUserId || !user ? localDisplayPhotoUrl : undefined);
    const mediaKey = (user as any)?.displayPhotoMedia?.keyBase64;
    // Only use global displayPhotoKey for self; otherwise require a per-user key entry
    const key =
      mediaKey ||
      profilePhotoEntry?.keyBase64 ||
      (user?.userId && user.userId === fallbackUserId ? displayPhotoKey : undefined) ||
      undefined;
    return { cachePath, url: urlFallback, key };
  }, [src, user?.displayPhoto, (user as any)?.displayPhotoMedia?.keyBase64, profilePhotoEntry, displayPhotoKey, localDisplayPhotoUrl, fallbackUserId, user?.userId]);
  const [profilePhoto, setProfilePhoto] = useState(
    src || "default-profile.png"
  );

  useEffect(() => {
    const log = (...args: any[]) => {
      console.log("[UserProfilePicture]", ...args);
    };
    const loadLocalImage = async (path: string) => {
      try {
        const blob = await filesystem.readFileToBlob?.({ path });
        if (blob) {
          const blobUrl = URL.createObjectURL(blob);
          setProfilePhoto(blobUrl);
        } else {
          setProfilePhoto("default-profile.png");
        }
      } catch (err) {
        console.warn("[UserProfilePicture] failed to read local image", err);
        setProfilePhoto("default-profile.png");
      }
    };

    log("render", {
      src,
      userDisplayPhoto: user?.displayPhoto,
      profilePhotoEntry,
      hasKey: !!displayPhotoKey,
    });

    const targetUrl = effectivePhoto.cachePath || effectivePhoto.url;
    const keyBase64 = effectivePhoto.key;
    let aborted = false;
    let abortController: AbortController | null = null;

    (async () => {
      try {
        if (src) {
          setProfilePhoto(src);
          return;
        }

        if (profilePhotoEntry?.cachePath) {
          try {
            const cached = await filesystem.readFileToBlob?.({
              path: profilePhotoEntry.cachePath,
            });
            if (cached) {
              log("using cached decrypted file", profilePhotoEntry.cachePath);
              const blobUrl = URL.createObjectURL(cached);
              if (!aborted) setProfilePhoto(blobUrl);
              return;
            }
          } catch (err) {
            console.warn("[UserProfilePicture] failed to read cached decrypted file", err);
          }
        }

        if (
          user?.userId &&
          !profilePhotoEntry?.cachePath &&
          profilePhotoEntry?.url &&
          profilePhotoEntry?.keyBase64
        ) {
          try {
            const cachePath = await ensureProfilePhotoCached(profilePhotoEntry);
            if (cachePath) {
              await loadLocalImage(cachePath);
              return;
            }
          } catch (err) {
            console.warn("[UserProfilePicture] failed to decrypt profile photo", err);
          }
        }

        // If we have a cache path, use it; otherwise, only use remote if no cache and no enc
        if (targetUrl) {
          if (targetUrl.startsWith("profilephoto/") || targetUrl.startsWith("data:")) {
            log("loading local cache path", targetUrl);
            await loadLocalImage(targetUrl);
            return;
          } else if (targetUrl.startsWith("http")) {
            if (targetUrl.endsWith(".enc")) {
              log("enc photo but no cache path available; using default");
              setProfilePhoto("default-profile.png");
              return;
            } else {
              log("using remote/displayPhoto (non-enc)", targetUrl);
              setProfilePhoto(targetUrl);
              return;
            }
          }
        }

        log("no display photo, using default");
        setProfilePhoto("default-profile.png");
      } catch (err) {
        console.warn("[UserProfilePicture] failed to load profile photo", err);
        setProfilePhoto("default-profile.png");
      }
    })();

    return () => {
      aborted = true;
      if (abortController) abortController.abort();
    };
  }, [
    src,
    effectivePhoto.url,
    effectivePhoto.key,
    profilePhotoEntry?.cachePath,
    displayPhotoKey,
    user,
    fallbackUserId,
  ]);

  const handleImageError = () => {
    setProfilePhoto("default-profile.png");
  };

  const size = width || 32;

  if (isCommunity) {
    return (
      <div
        className="rounded-full bg-muted flex items-center justify-center"
        style={{ width: size, height: size, ...style }}
      >
        <Home className="w-1/2 h-1/2 text-muted-foreground" />
      </div>
    );
  } else if (isGroup) {
    return (
      <div
        className="rounded-full bg-muted flex items-center justify-center"
        style={{ width: size, height: size, ...style }}
      >
        <Users className="w-1/2 h-1/2 text-muted-foreground" />
      </div>
    );
  } else if (user?.username && (user as any)?.displayPhoto?.notEnc?.()) {
    return <InitialProfilePicture size={width} name={user.username} />;
  } else {
    return (
      <img
        className="rounded-full object-cover"
        width={size}
        height={size}
        src={profilePhoto}
        onError={handleImageError}
        alt="User profile"
        style={style}
      />
    );
  }
}
